import { useCallback, useMemo, useState } from 'react'

import { distance } from 'fastest-levenshtein'
import { compact, sortBy } from 'lodash'

type FuzzyMatch<T> = {
  item: T
} & ({ type: 'inclusion' } | { type: 'levenshtein'; distance: number })

export const useFuzzySearch = <T>(
  items: T[],
  matchFunction: (
    item: T,
  ) => Array<
    { data: string | undefined } & (
      { type: 'inclusion' } | { type: 'levenshtein'; distanceThreshold: number }
    )
  >,
) => {
  const [filter, setFilter] = useState('')

  const updateFilter = useCallback((value: string) => {
    setFilter(value.trim())
  }, [])

  const results = useMemo(() => {
    const lowerFilter = filter.toLowerCase()

    const matchesWithDistance = compact(
      items.map((item): FuzzyMatch<T> | undefined => {
        const matchDefinitions = matchFunction(item)

        // eslint-disable-next-line no-loops/no-loops
        for (const definition of matchDefinitions) {
          if (!definition.data) return undefined

          const lowerData = definition.data.toLowerCase()

          if (definition.type === 'inclusion') {
            if (lowerData.includes(lowerFilter)) {
              return { item, type: 'inclusion' }
            }
          }

          if (definition.type === 'levenshtein') {
            const matchDistance =
              distance(lowerFilter, lowerData) /
              Math.max(lowerFilter.length, lowerData.length)

            if (matchDistance < definition.distanceThreshold) {
              return { item, type: 'levenshtein', distance: matchDistance }
            }
          }
        }
      }),
    )

    return sortBy(matchesWithDistance, (match) => {
      if (match.type === 'inclusion') {
        return -1
      } else {
        return match.distance
      }
    }).map(({ item }) => item)
    // Let's assume matchFunction doesn't change instead of relying in on it using useCallback
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, items])

  return { filter, updateFilter, results }
}
